// eslint-disable-next-line no-restricted-imports -- This IS the sanitize facade; it wraps dompurify.
import DOMPurify, {type Config as DOMPurifyConfig} from 'dompurify'

import type {SafeHTMLString} from './safe-html-string'
import type {Purifier} from './types'

/**
 * Sanitize HTML using DOMPurify with the browser's native window.
 *
 * This is the browser entry point — on the server, the "node" condition
 * in package.json exports resolves to sanitize.server.ts instead.
 *
 * @param html - The HTML string to sanitize
 * @param config - Optional DOMPurify Config object.
 */
export function sanitize(html: string | Node, config?: DOMPurifyConfig): SafeHTMLString {
  if (!config) {
    return DOMPurify.sanitize(html, {
      RETURN_TRUSTED_TYPE: false,
      IN_PLACE: false,
    }) as SafeHTMLString
  }

  return DOMPurify.sanitize(html, {
    ...config,
    RETURN_TRUSTED_TYPE: false,
    IN_PLACE: false,
  }) as SafeHTMLString
}

/**
 * Create a DOMPurify instance for advanced usage (e.g., adding hooks).
 * The returned purifier's `.sanitize()` is typed to return `SafeHTMLString`
 * for standard string sanitization calls.
 */
export function createPurifier(cb?: (purifier: Purifier) => void): Purifier {
  const purifier = DOMPurify() as unknown as Purifier
  if (cb) {
    cb(purifier)
  }
  return purifier
}

export function sanitizeWithPurifier(
  html: string | Node,
  purifier: Purifier,
  config?: DOMPurifyConfig,
): SafeHTMLString {
  if (!config) {
    return purifier.sanitize(html, {
      RETURN_TRUSTED_TYPE: false,
      IN_PLACE: false,
    })
  }

  return purifier.sanitize(html, {
    ...config,
    RETURN_TRUSTED_TYPE: false,
    IN_PLACE: false,
  })
}

// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {Config, Purifier, UponSanitizeAttributeHookEvent} from './types'
